import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import styled from "styled-components";

import Message from "./Message";
import MessageLoading from "./MessageLoading";
import MessageLoadingThink from "./MessageLoadingThink";

type ChatLogProps = {
    role: "user" | "assistant";
    content: string;
}[];

export interface ChatLogHandle {
    scrollToBottom: (behavior?: "smooth" | "auto") => void;
}

const ChatLog = forwardRef(({ data, isLoading = false }: { data: ChatLogProps; isLoading?: boolean }, ref) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isLongLoading, setIsLongLoading] = useState(false);

    const scrollToBottom = (behavior: "smooth" | "auto" = "smooth") => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
                top: scrollContainerRef.current.scrollHeight,
                behavior: behavior,
            });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [data]);

    useEffect(() => {
        if (isLoading) {
            const timer = setTimeout(() => {
                setIsLongLoading(true);
            }, 5000);

            return () => clearTimeout(timer);
        } else {
            setIsLongLoading(false);
        }
    }, [isLoading]);

    useImperativeHandle(ref, () => ({
        scrollToBottom,
    }));

    return (
        <ChatLogContainer ref={scrollContainerRef}>
            {data.map((item, index) => item.content !== "" && <Message key={index} role={item.role} content={item.content} />)}

            {isLoading && !isLongLoading && <MessageLoading />}
            {isLoading && isLongLoading && <MessageLoadingThink />}
        </ChatLogContainer>
    );
});

ChatLog.displayName = "ChatLog";

const ChatLogContainer = styled.div`
    flex-grow: 1;
    overflow-y: scroll;
    overscroll-behavior: contain;
    padding: 26px 0 10px 0;
    padding-right: 8px;
`;

export default ChatLog;
